// Physical balance remains a 1C fact. An explicit server verdict can permit an
// ordinary sale independently of that balance under the administrative policy.
import {isServerSnapshotTimeValid} from './serverSnapshotTime';

export const MAIN_WAREHOUSE_CODE = 'f8bbc32b-8b9e-11ee-80c1-fa163e0fa166';

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const uniqueNamedSize = (rows, sizeName) => {
  if (!Array.isArray(rows)) return null;
  const matching = rows.filter(row => isRecord(row) && row.size_name === sizeName);
  return matching.length === 1 ? matching[0] : null;
};
const positiveId = value => {
  if (typeof value !== 'number' && (typeof value !== 'string' || !/^\d+$/.test(value))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};
const validIso = value => typeof value === 'string' &&
  /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(value) &&
  Number.isFinite(Date.parse(value)) && new Date(Date.parse(value)).toISOString() === value;
const boundedText = (value, limit = 256) => typeof value === 'string' &&
  value.length > 0 && value.length <= limit && value.trim() === value;
const nonblankText = (value, limit = 256) => typeof value === 'string' &&
  value.length > 0 && value.length <= limit && /\S/.test(value);
const normalizedName = value => value.normalize('NFKC').trim().toUpperCase();
const denseArray = (value, max) => {
  if (!Array.isArray(value) || value.length === 0 || value.length > max) return false;
  for (let index = 0; index < value.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(value, index)) return false;
  }
  return true;
};
const physicalGiftArticles = new Map([[227, 'gift'], [231, 'gift1'], [232, 'gift2'],
  [233, 'gift3'], [1171, 'gift4']]);
const isPhysicalGiftCertificate = product => product?.electron_sertificate === false &&
  Number.isSafeInteger(product.id) && physicalGiftArticles.has(product.id) &&
  typeof product.articul === 'string' &&
  physicalGiftArticles.get(product.id) === product.articul.trim().toLowerCase();

// `shadow` observes policy without changing old storefront behavior. Any
// present non-shadow object is treated as an enforcement attempt and therefore
// cannot silently fall back to raw pivot stocks if its schema is malformed.
export const usesEnforcedCommerce = child => child?.commerce !== undefined &&
  (!isRecord(child.commerce) || child.commerce.mode !== 'shadow');

const validCommerceStock = stock => {
  if (!isRecord(stock) || !['confirmed', 'stale', 'unknown', 'not_applicable'].includes(stock.state)) return false;
  if (stock.state === 'confirmed') {
    return Number.isSafeInteger(stock.quantity) && stock.quantity >= 0 &&
      validIso(stock.sourceAsOf) && validIso(stock.validUntil) &&
      Date.parse(stock.sourceAsOf) <= Date.parse(stock.validUntil);
  }
  return stock.quantity === null && stock.validUntil === null &&
    (stock.sourceAsOf === null || validIso(stock.sourceAsOf));
};
const validPaymentOptions = options => {
  if (!Array.isArray(options) || options.length > 2) return false;
  const seen = new Set();
  for (let index = 0; index < options.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(options, index) ||
        !['payment_now', 'payment_later'].includes(options[index]) || seen.has(options[index])) return false;
    seen.add(options[index]);
  }
  return true;
};

const validCommerceDescriptor = row => {
  if (!isRecord(row) || !Number.isSafeInteger(row.childProductId) || row.childProductId <= 0 ||
      !Number.isSafeInteger(row.sizeId) || row.sizeId <= 0 ||
      !nonblankText(row.sizeName, 128) || !nonblankText(row.offerId, 255) ||
      !['ordinary', 'certificate'].includes(row.kind) ||
      !['buy_now', 'preorder', 'notify', 'none'].includes(row.action) ||
      typeof row.checkoutAllowed !== 'boolean' ||
      !Number.isSafeInteger(row.maxQuantity) || row.maxQuantity < 0 ||
      !boundedText(row.reasonCode, 128) || row.policyVersion !== 'sku-commerce-v1' ||
      !validCommerceStock(row.stock) || !validPaymentOptions(row.paymentOptions) ||
      (row.dispatchEta !== null && (!isRecord(row.dispatchEta) ||
        !validIso(row.dispatchEta.from) || !validIso(row.dispatchEta.to) ||
        Date.parse(row.dispatchEta.to) < Date.parse(row.dispatchEta.from)))) return false;
  if (row.kind === 'certificate') {
    return row.action === 'none' && row.checkoutAllowed === false && row.maxQuantity === 0 &&
      row.stock.state === 'not_applicable' && row.stock.sourceAsOf === null &&
      isRecord(row.certificate) && row.certificate.flow === 'existing_certificate' &&
      typeof row.certificate.available === 'boolean';
  }
  const physicalGift = row.reasonCode === 'physical_gift_no_stock' &&
    row.stock.state === 'not_applicable' && row.stock.sourceAsOf === null;
  if (row.certificate !== undefined || (row.stock.state === 'not_applicable' && !physicalGift)) return false;
  if (row.action === 'buy_now') return row.checkoutAllowed === true && row.maxQuantity > 0 &&
    ((row.reasonCode === 'admin_sale_allowed' && row.maxQuantity <= 2147483647) ||
      (physicalGift && row.maxQuantity <= 2147483647) ||
      (row.reasonCode === 'stock_confirmed' && row.stock.state === 'confirmed' && row.maxQuantity <= row.stock.quantity));
  if (row.action === 'preorder') return row.checkoutAllowed === true && row.maxQuantity > 0;
  return row.checkoutAllowed === false && row.maxQuantity === 0;
};

const enforcedSize = (child, sizeName) => {
  const envelope = child?.commerce;
  if (!isRecord(envelope) || envelope.mode !== 'enforced' ||
      envelope.schemaVersion !== 'public-sku-v1' || !validIso(envelope.evaluatedAt) ||
      !denseArray(envelope.sizes, 256) || !denseArray(child.child_product_size_field, 256)) return null;
  const childId = child.id;
  if (!Number.isSafeInteger(childId) || childId <= 0) return null;
  const names = new Set(), sizeIds = new Set(), offerIds = new Set();
  for (const row of envelope.sizes) {
    const name = isRecord(row) && typeof row.sizeName === 'string' ? normalizedName(row.sizeName) : null;
    if (!validCommerceDescriptor(row) || row.childProductId !== childId ||
        (row.stock.state === 'confirmed' &&
          (Date.parse(row.stock.sourceAsOf) > Date.parse(envelope.evaluatedAt) ||
            Date.parse(row.stock.validUntil) < Date.parse(envelope.evaluatedAt))) ||
        names.has(name) || sizeIds.has(row.sizeId) || offerIds.has(row.offerId)) return null;
    names.add(name); sizeIds.add(row.sizeId); offerIds.add(row.offerId);
  }
  const fieldNames = new Set(), fieldIds = new Set();
  for (const field of child.child_product_size_field) {
    if (!isRecord(field) || !nonblankText(field.size_name, 128) || typeof field.isActive !== 'boolean' ||
        (field.available !== undefined && typeof field.available !== 'boolean')) return null;
    const name = normalizedName(field.size_name);
    if (fieldNames.has(name)) return null;
    fieldNames.add(name);
    let boundId;
    for (const key of ['id', 'sizeId']) {
      if (!Object.prototype.hasOwnProperty.call(field, key)) continue;
      const id = field[key];
      if (!Number.isSafeInteger(id) || id <= 0 || (boundId !== undefined && boundId !== id)) return null;
      boundId = id;
    }
    if (boundId !== undefined && fieldIds.has(boundId)) return null;
    if (boundId !== undefined) fieldIds.add(boundId);
  }
  const field = uniqueNamedSize(child.child_product_size_field, sizeName);
  const descriptor = envelope.sizes.find(row => row.sizeName === sizeName);
  if (!field || field.isActive !== true || !descriptor ||
      (Object.prototype.hasOwnProperty.call(field, 'id') && field.id !== descriptor.sizeId) ||
      (Object.prototype.hasOwnProperty.call(field, 'sizeId') && field.sizeId !== descriptor.sizeId)) return null;
  return {field, descriptor};
};

/**
 * A display view of the same validated verdict used by the basket guard.
 * Paid preorder is deliberately not a supported client action yet. Shadow and
 * absent DTOs stay legacy; a present malformed enforcement attempt never does.
 * @returns {{mode: 'legacy'|'enforced', action: 'buy_now'|'certificate'|'notify',
 *   available: boolean, canNotify: boolean, stockPending: boolean, isAnnouncement: boolean, expiresAt: number|null,
 *   descriptor: object|null, stockState: string|null}}
 */
export function getSizeCommerceView(child, sizeName, requestedQuantity = 1) {
  if (!usesEnforcedCommerce(child)) {
    const available = isOrdinarySizeAvailable(child, sizeName, requestedQuantity);
    const field = uniqueNamedSize(child?.child_product_size_field, sizeName);
    const networkSale = field?.networkStockSale;
    const expiresAt = isRecord(networkSale) && validIso(networkSale.validUntil) ? Date.parse(networkSale.validUntil) : null;
    return {mode: 'legacy', action: available ? 'buy_now' : 'notify', available,
      canNotify: Boolean(sizeName), stockPending: false, isAnnouncement: false, expiresAt, descriptor: null, stockState: null};
  }
  const published = child?.is_active === true && child.product?.active_product === true;
  const selected = enforcedSize(child, sizeName);
  const row = selected?.descriptor;
  const adminSale = row?.kind === 'ordinary' && row.reasonCode === 'admin_sale_allowed';
  const physicalGift = row?.kind === 'ordinary' && row.reasonCode === 'physical_gift_no_stock' &&
    row.stock.state === 'not_applicable' && isPhysicalGiftCertificate(child.product);
  const now = Date.now();
  const expiresAt = !adminSale && row?.stock.state === 'confirmed' ? Date.parse(row.stock.validUntil) : null;
  const snapshotTimeValid = isServerSnapshotTimeValid(child?.commerce?.evaluatedAt, now);
  const current = snapshotTimeValid &&
    (expiresAt === null || expiresAt >= now);
  const quantityValid = Number.isSafeInteger(requestedQuantity) && requestedQuantity > 0;
  const ordinary = row?.kind === 'ordinary' && child.product?.electron_sertificate !== true;
  const isAnnouncement = Boolean(row?.action === 'notify' && published && ordinary && selected && child.coming_soon === true &&
    snapshotTimeValid);
  const stockPending = Boolean(row?.action !== 'preorder' && !adminSale && !isAnnouncement && published && ordinary && selected && snapshotTimeValid &&
    (['unknown', 'stale'].includes(row.stock.state) ||
      (expiresAt !== null && expiresAt < now)));
  const buyNow = published && current && quantityValid && ordinary &&
    row.action === 'buy_now' && row.checkoutAllowed === true && row.maxQuantity >= requestedQuantity &&
    (adminSale || physicalGift || row.stock.quantity >= requestedQuantity);
  const certificate = published && current && quantityValid && child.product?.electron_sertificate === true &&
    row?.kind === 'certificate' && row.certificate.available === true && selected.field.available === true;
  const preorder = published && current && quantityValid && ordinary && selected &&
    row.action === 'preorder' && row.checkoutAllowed === true && row.maxQuantity >= requestedQuantity;
  return {mode: 'enforced', action: certificate ? 'certificate' : buyNow ? 'buy_now' : preorder ? 'preorder' : 'notify',
    available: Boolean(buyNow || certificate),
    canNotify: Boolean(!stockPending && published && ordinary && selected && snapshotTimeValid),
    stockPending, isAnnouncement, expiresAt, descriptor: row || null, stockState: row?.stock.state || null};
}
const hasAmbiguousIdentity = (rows, selected, read) => {
  const raw = read(selected);
  if (raw === undefined) return false; // Legacy public DTOs can omit identity fields.
  const id = positiveId(raw);
  return id === null || rows.filter(row => positiveId(read(row)) === id).length !== 1;
};
const selectedPivotSize = (child, sizeName) => {
  const rows = child.child_product_size;
  const size = uniqueNamedSize(rows, sizeName);
  if (!size || (size.isActive !== undefined && size.isActive !== true)) return null;
  const pivot = size.childProduct_Size;
  if (!isRecord(pivot)) return null;
  if (hasAmbiguousIdentity(rows, size, row => row?.id) ||
      hasAmbiguousIdentity(rows, size, row => row?.childProduct_Size?.id) ||
      hasAmbiguousIdentity(rows, size, row => row?.childProduct_Size?.sizeId)) return null;
  if (pivot.childProductId !== undefined && positiveId(pivot.childProductId) !== positiveId(child.id)) return null;
  if (pivot.sizeId !== undefined && size.id !== undefined && positiveId(pivot.sizeId) !== positiveId(size.id)) return null;
  return size;
};

export function parseWarehouseStocks(raw) {
  let value = raw;
  for (let attempt = 0; attempt < 2 && typeof value === 'string'; attempt += 1) {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value;
}

export function getConfirmedMainWarehouseQuantity(size) {
  const stocks = parseWarehouseStocks(size?.childProduct_Size?.stocks);
  if (!stocks || !Object.prototype.hasOwnProperty.call(stocks, MAIN_WAREHOUSE_CODE)) {
    return null;
  }
  const rawQuantity = stocks[MAIN_WAREHOUSE_CODE];
  if (typeof rawQuantity !== 'number' &&
      (typeof rawQuantity !== 'string' || !/^\d+$/.test(rawQuantity))) {
    return null;
  }
  const quantity = Number(rawQuantity);
  return Number.isSafeInteger(quantity) && quantity >= 0 ? quantity : null;
}

export function isOrdinarySizeAvailable(child, sizeName, requestedQuantity = 1) {
  if (!child?.id || typeof sizeName !== 'string' || !sizeName.trim() ||
      child.is_active !== true || child.product?.active_product !== true ||
      !Number.isSafeInteger(requestedQuantity) || requestedQuantity <= 0) {
    return false;
  }

  if (usesEnforcedCommerce(child)) {
    return getSizeCommerceView(child, sizeName, requestedQuantity).action === 'buy_now';
  }

  if (child.coming_soon === true) return false;

  const sizeField = uniqueNamedSize(child.child_product_size_field, sizeName);
  if (sizeField?.isActive !== true) return false;

  // Phase-one basket hydration is still legacy on the site. This fresh
  // server-owned hint carries the exact physical gift verdict into that lane.
  if (sizeField.physicalGiftSaleAllowed === true) {
    return isPhysicalGiftCertificate(child.product) && requestedQuantity <= 2147483647;
  }

  // A legacy basket line can carry the server's current network-stock verdict.
  // Its presence overrides the old internet-warehouse-only check, including
  // a zero verdict, and expires with the stock snapshot.
  if (Object.prototype.hasOwnProperty.call(sizeField, 'networkStockSale')) {
    const sale = sizeField.networkStockSale;
    return isRecord(sale) && Number.isSafeInteger(sale.maxQuantity) &&
      sale.maxQuantity > 0 && validIso(sale.validUntil) &&
      Date.parse(sale.validUntil) >= Date.now() && requestedQuantity <= sale.maxQuantity;
  }

  // The server may attach its current administrative sale verdict to a legacy
  // basket item while the established checkout and payment flow remains active.
  // This bit is separate from the historical stock-derived `available` field.
  if (Object.prototype.hasOwnProperty.call(sizeField, 'adminSaleAllowed')) {
    return sizeField.adminSaleAllowed === true && requestedQuantity <= 2147483647;
  }

  const size = selectedPivotSize(child, sizeName);
  return getConfirmedMainWarehouseQuantity(size) >= requestedQuantity;
}

export function isBasketLineAvailable(line) {
  const child = line?.item;
  const sizeName = line?.currentSize;
  const quantity = line?.count;
  const isCertificate = child?.product?.electron_sertificate === true;
  if (!isCertificate) {
    return isOrdinarySizeAvailable(child, sizeName, quantity);
  }
  if (typeof sizeName !== 'string' || !sizeName.trim()) return false;
  if (usesEnforcedCommerce(child)) {
    return getSizeCommerceView(child, sizeName, quantity).action === 'certificate';
  }
  const sizeField = uniqueNamedSize(child.child_product_size_field, sizeName);
  // Older certificate DTOs omit the pivot collection. When it is present,
  // missing or ambiguous selected identities cannot grant manual availability.
  if (child.child_product_size !== undefined && !selectedPivotSize(child, sizeName)) return false;
  return child.is_active === true && child.product.active_product === true &&
    sizeField?.isActive === true && sizeField.available === true &&
    Number.isSafeInteger(quantity) && quantity > 0;
}
